import React from "react";
import EventPill from "./EventPill";
import { formatDate } from "../../utils/dateUtils";

const CalendarDayCell = ({
  dayObj,
  index,
  selectedDates,
  vacationDates,
  dateRangeStart,
  dateRangeEnd,
  dateRangeMode,
  getEventsForDate,
  styles,
  screenSize,
  language,
  t,
  onDateClick,
  onEventClick,
  onShowMoreEvents,
}) => {
  const date = dayObj.date ? new Date(dayObj.date) : null;

  if (!date) {
    return <div key={index} />;
  }

  const dateString = formatDate(date);
  const isSelected = selectedDates.has(dateString);
  const isToday = date.toDateString() === new Date().toDateString();
  const isVacation = vacationDates.has(dateString);
  const dayEvents = getEventsForDate(date);

  const isInRange =
    dateRangeStart &&
    dateRangeEnd &&
    date >= dateRangeStart &&
    date <= dateRangeEnd;

  const hasEvents = dayEvents.length > 0;

  // Room for 3 pills on desktop / 2 on tablet / 1 on phones — the cell is
  // shorter than the old 120px tile, so anything beyond that collapses into
  // the "+N more" affordance rather than overflowing.
  const maxPills = screenSize.isMobile ? 1 : screenSize.isTablet ? 2 : 3;
  const hiddenCount = dayEvents.length - maxPills;

  return (
    <div
      className="cal-day"
      style={styles.calendarDay(
        dayObj,
        isSelected,
        isToday,
        isVacation,
        isInRange,
        getEventsForDate,
        dateRangeMode
      )}
      onClick={() => onDateClick(date)}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
        <div
          style={styles.calendarDayNumber(
            isSelected,
            isToday,
            isVacation,
            hasEvents,
            dayObj.isCurrentMonth
          )}
        >
          {date.getDate()}
        </div>

        {/* On phones a pill barely fits, so the day's events are summarised
            as coloured dots next to the number instead. */}
        {screenSize.isMobile && hasEvents && (
          <div style={{ display: "flex", gap: "3px", alignItems: "center" }}>
            {dayEvents.slice(0, 3).map((event) => (
              <span
                key={event.id}
                style={{
                  width: "5px",
                  height: "5px",
                  borderRadius: "999px",
                  background: event.color,
                  display: "inline-block",
                }}
              />
            ))}
          </div>
        )}

        {isVacation && (
          <span
            title={t.vacation}
            style={{
              width: "6px",
              height: "6px",
              borderRadius: "999px",
              background: "#06b6d4",
              marginInlineStart: "auto",
              flexShrink: 0,
            }}
          />
        )}

        {/* Hover-only hint that an empty day is clickable — the cells no
            longer look like buttons, so the affordance has to come from
            somewhere. */}
        {!screenSize.isMobile && !isVacation && (
          <span className="cal-add" aria-hidden="true">
            +
          </span>
        )}
      </div>

      {!screenSize.isMobile &&
        dayEvents.slice(0, maxPills).map((event) => (
          <EventPill
            key={event.id}
            event={event}
            styles={styles}
            screenSize={screenSize}
            language={language}
            t={t}
            onEventClick={onEventClick}
          />
        ))}

      {!screenSize.isMobile && hiddenCount > 0 && (
        <button
          type="button"
          className="cal-more"
          onClick={(e) => {
            e.stopPropagation();
            onShowMoreEvents?.(date, dayEvents);
          }}
          style={styles.moreEventsButton}
        >
          +{hiddenCount} {t.more}
        </button>
      )}
    </div>
  );
};

export default CalendarDayCell;
